import { useEffect, useRef, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'stratsync.backgroundMedia';
const CHANGE_EVENT = 'stratsync:background-media-change';
export type BackgroundMediaMode = 'video' | 'static';
let memoryMode: BackgroundMediaMode = 'video';
let storageBlocked = false;

function readMode(): BackgroundMediaMode {
    if (storageBlocked) return memoryMode;
    try { return localStorage.getItem(STORAGE_KEY) === 'static' ? 'static' : 'video'; }
    catch { return memoryMode; }
}
function subscribe(listener: () => void) {
    window.addEventListener(CHANGE_EVENT, listener);
    window.addEventListener('storage', listener);
    return () => {
        window.removeEventListener(CHANGE_EVENT, listener);
        window.removeEventListener('storage', listener);
    };
}
export function useBackgroundMediaPreference() {
    const mode = useSyncExternalStore(subscribe, readMode, () => 'video' as const);
    const setMode = (next: BackgroundMediaMode) => {
        memoryMode = next;
        try { localStorage.setItem(STORAGE_KEY, next); } catch { storageBlocked = true; }
        window.dispatchEvent(new Event(CHANGE_EVENT));
    };
    return { mode, setMode };
}

/** Decorative media only; user-controlled players must not use this hook. */
export function useBackgroundMedia(src: string, poster: string) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const { mode } = useBackgroundMediaPreference();
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const connection = (navigator as Navigator & { connection?: EventTarget & { saveData?: boolean } }).connection;
        let visible = true;
        let failed = false;
        let disposed = false;
        video.poster = poster;
        video.muted = true;
        video.playsInline = true;
        video.loop = true;
        video.preload = 'metadata';
        video.playbackRate = 1;
        const release = () => {
            video.pause();
            if (video.hasAttribute('src')) {
                video.removeAttribute('src');
                video.load();
            }
        };
        const update = () => {
            if (disposed) return;
            if (mode === 'static' || motion.matches || connection?.saveData || failed) {
                release();
                return;
            }
            if (document.hidden || !visible) {
                video.pause();
                return;
            }
            // Avoid aborting the initial request during StrictMode effect replay.
            if (video.getAttribute('src') !== src) video.setAttribute('src', src);
            void video.play().catch((error: unknown) => {
                if (error instanceof DOMException && error.name === 'AbortError') return;
                if (!disposed && !document.hidden && visible) {
                    failed = true;
                    release();
                }
            });
        };
        const onError = () => { failed = true; update(); };
        const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
            visible = entries[0]?.isIntersecting ?? true;
            update();
        });
        observer?.observe(video);
        motion.addEventListener('change', update);
        connection?.addEventListener('change', update);
        document.addEventListener('visibilitychange', update);
        video.addEventListener('error', onError);
        update();
        return () => {
            disposed = true;
            video.pause();
            observer?.disconnect();
            motion.removeEventListener('change', update);
            connection?.removeEventListener('change', update);
            document.removeEventListener('visibilitychange', update);
            video.removeEventListener('error', onError);
        };
    }, [mode, src, poster]);
    return { videoRef };
}
